/**
 * CareLogix™ ORACLE — Executive Briefing Center (Phase 42D).
 *
 * Single executive destination summarising every intelligence domain at a
 * glance. Member-facing — operator/AdminOne dashboards live under /adminone/*.
 *
 * Strictly additive. Uses the unified member session for personalization;
 * never claims certainty or live data when none is available.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useOracleMemberSession } from "@/hooks/useOracleMemberSession";
import { useOracleMode } from "@/hooks/useOracleMode";

const C = {
  bg:        "#0a0f0a",
  panel:     "rgba(13,26,15,0.85)",
  ivory:     "#f5f5dc",
  ivoryD:    "#d4d4aa",
  gold:      "#facc15",
  emerald:   "#7AC860",
  border:    "rgba(245,200,66,0.30)",
};

const S = {
  wrap:   { background: C.bg, minHeight: "100vh", color: C.ivory, padding: "28px 22px", fontFamily: "'Inter', system-ui, sans-serif" },
  kicker: { color: C.gold, fontSize: 11, letterSpacing: "0.22em", fontWeight: 800, textTransform: "uppercase" },
  title:  { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 28, margin: "6px 0 6px" },
  sub:    { color: C.ivoryD, fontSize: 15, maxWidth: 760, lineHeight: 1.55, margin: "0 0 22px" },
  grid:   { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 },
  card:   { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 },
  ct:     { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 17, margin: "0 0 6px" },
  cs:     { color: C.ivoryD, fontSize: 13, lineHeight: 1.55, margin: "0 0 10px" },
  row:    { display: "flex", justifyContent: "space-between", color: C.ivoryD, fontSize: 12, marginTop: 6 },
  link:   { color: C.gold, fontSize: 12, fontWeight: 600, textDecoration: "none" },
  disc:   { color: C.ivoryD, fontSize: 11.5, marginTop: 24, lineHeight: 1.55, opacity: 0.85 },
};

const MODULES = [
  { key: "daily",     title: "Daily Briefing",         what: "Today's domain pulse — Health, Capital, Sports, Grants.", why: "Catch material shifts before market open.",          next: "Review by 9:00 AM local time.", to: "/oracle/dashboard"      , testid: "module-daily" },
  { key: "weekly",    title: "Weekly Executive Brief", what: "Top 5 moves across every domain.",                       why: "Set strategic priorities for the week ahead.",        next: "Review every Sunday evening.",   to: "/oracle/dashboard"      , testid: "module-weekly" },
  { key: "capital",   title: "Capital Snapshot",       what: "Markets, allocation drift, opportunity windows.",        why: "Detect repositioning windows early.",                 next: "Re-review on 5% move events.",    to: "/oracle/capital"        , testid: "module-capital" },
  { key: "health",    title: "Health Snapshot",        what: "LifeScore™ trend, recovery, FoodRx™ summary.",          why: "Sustain executive performance.",                      next: "Weekly check; deeper on dips.",   to: "/oracle/lifescore"      , testid: "module-health" },
  { key: "grants",    title: "Grant Opportunities",    what: "Newly opened/closing grants matching your profile.",     why: "Funding windows are time-bound.",                     next: "Re-review weekly.",               to: "/oracle/capital"        , testid: "module-grants" },
  { key: "research",  title: "Research Highlights",    what: "Curated research signals via ORACLE Research Engine™.", why: "Stay ahead of academic + market evidence.",           next: "Re-review weekly.",               to: "/oracle/research"       , testid: "module-research" },
  { key: "risk",      title: "Risk Outlook",           what: "Cross-domain risk signals (regulatory, market, health).", why: "Surface the risks worth acting on.",                  next: "Daily glance, weekly deep-dive.", to: "/oracle/risk"           , testid: "module-risk" },
  { key: "summary",   title: "Executive Summary PDF",  what: "One-page export of this week's briefing.",               why: "Share with chief-of-staff / board.",                  next: "Generate Friday afternoon.",      to: "/oracle/reports"        , testid: "module-summary" },
];

export default function OracleBriefingCenterPage() {
  const { member } = useOracleMemberSession();
  const { isSimple } = useOracleMode();
  const greeting = useMemo(() => {
    if (!member) return "Welcome to your Briefing Center";
    const name = (member.full_name || "Member").split(" ")[0];
    return `${greetingByHour()}, ${name}`;
  }, [member]);

  return (
    <main style={S.wrap} data-testid="oracle-briefing-center-page">
      <div style={S.kicker}>ORACLE · EXECUTIVE BRIEFING CENTER</div>
      <h1 style={S.title}>{greeting}</h1>
      <p style={S.sub}>
        One destination for every intelligence domain. Each module tells you <strong style={{ color: C.ivory }}>what changed</strong>,
        <strong style={{ color: C.ivory }}> why it matters</strong>, and <strong style={{ color: C.ivory }}>when to review next</strong> — in plain language.
        {!isSimple && " Advanced Mode reveals provider telemetry, reliability metrics, and core-satellite topology in each card."}
      </p>

      <section style={S.grid} aria-label="Briefing modules">
        {MODULES.map((m) => (
          <article key={m.key} style={S.card} data-testid={m.testid}>
            <h2 style={S.ct}>{m.title}</h2>
            <p style={S.cs}><strong style={{ color: C.ivory }}>What changed:</strong> {m.what}</p>
            <p style={S.cs}><strong style={{ color: C.ivory }}>Why it matters:</strong> {m.why}</p>
            <p style={S.cs}><strong style={{ color: C.ivory }}>Recommended next review:</strong> {m.next}</p>
            <Link to={m.to} style={S.link} data-testid={`${m.testid}-open`}>Open →</Link>
          </article>
        ))}
      </section>

      <p style={S.disc} data-testid="briefing-disclaimer">
        Research and decision-support only. No autonomous medical, financial, trading, wagering, or governance actions.
        Human confirmation required.
      </p>
    </main>
  );
}

function greetingByHour() {
  try {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  } catch { return "Welcome back"; }
}
